import React, { useCallback } from 'react';

import { CopyOutlined } from '@ant-design/icons';
import { Alert, Tooltip } from 'antd';

import MyTable, { MyTableHeader, MyTableBody, MyTableRow, MyTableCol } from 'components/MyTable';
import QuickViewDefault from 'v2/components/Shared/QuickView';
import Truncate from 'v2/components/Shared/TruncateText';
import { dateFormat } from 'v2/formatters/date';
import { InvoiceStatus, SubscriptionStatus, WebhookStatus } from 'v2/interfaces/webhook2';
import type { Webhook, WebhookHistory } from 'v2/interfaces/webhook2';

import { HistoryStatus } from './FilterWebhookHistory';
import { ALL_PRODUCTS_ID } from '../WebhookForm/Fields';

const HTTP_STATUS_STYLE: Record<string, string> = {
  'status-success': 'bg-[#388E3C]',
  'status-failed': 'bg-[#d32f2f]'
};

type IProps = {
  webhook: WebhookHistory;
  open: boolean;
  handleClose: () => void;
  url: Webhook;
};

export const QuickView = ({ webhook, open, handleClose, url }: IProps) => {
  const indexes = [
    { title: 'Code', ordering: false },
    { title: 'Resposta', ordering: false },
    { title: 'Data', ordering: false }
  ];

  const solveStatusColor = useCallback((status: string): string => {
    if (!status) return 'status-pending';

    const statusInt = Number.parseInt(status);

    if (statusInt >= 200 && statusInt < 300) {
      return 'status-success';
    }

    return 'status-failed';
  }, []);
  const historic = webhook?.sends || [];
  const sent = webhook?.sentData ? JSON.stringify(JSON.parse(webhook.sentData), undefined, 2) : '';

  const [sentData, setSentData] = React.useState<string>();

  const requestCount = parseInt(String(webhook?.count ?? 0));
  const address = webhook?.lastSend?.url || url?.url;

  const handleSentDataCopy = (e: React.SyntheticEvent, w: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(w);
    setSentData(w);

    setTimeout(() => {
      setSentData(null);
    }, 2000);
  };

  return (
    <QuickViewDefault title='Detalhes do Webhook' open={open} onClose={handleClose}>
      <div className='mb-1.5'>
        <strong>URL</strong>
        <div>{address}</div>
      </div>
      <div className='mb-1.5'>
        <Truncate count={50} final='...' title={webhook?.product.title}>
          <strong>Produto</strong>
          <div>{webhook?.product.id !== ALL_PRODUCTS_ID && `(${webhook?.product.id}) ${webhook?.product.title}`}</div>
        </Truncate>
      </div>
      <div className='mb-1.5'>
        <Truncate count={25} final='...' title={webhook?.customer.name}>
          <strong>Comprador</strong> <div>{webhook?.customer.name}</div>
        </Truncate>
      </div>

      {webhook?.type === 'invoice' && (
        <div className='mb-1.5'>
          <div>
            <strong>Fatura</strong>
            <div>
              {webhook?.entityId} - {InvoiceStatus[webhook.entityStatus as keyof typeof InvoiceStatus] ?? ''}
            </div>
          </div>
        </div>
      )}

      {webhook?.type === 'contract' && (
        <div className='mb-1.5'>
          <div>
            <strong>Contrato</strong>
            <div>
              {webhook?.entityId} - {SubscriptionStatus[webhook?.entityStatus as keyof typeof SubscriptionStatus]}
            </div>
          </div>
        </div>
      )}

      {requestCount > 0 && (
        <div className='mb-1.5'>
          <strong>Tentativas:&nbsp;</strong>
          {requestCount}
        </div>
      )}

      {url?.state === WebhookStatus.failing && webhook?.status === HistoryStatus.PENDING && (
        <div className='mt-6'>
          <Alert
            type='warning'
            className='text-sm'
            message={
              <>
                A sua url está <strong>falhando</strong>, para que este evento seja enviado para a sua URL{' '}
                <strong>(além de todos os outros eventos pendentes no histórico)</strong> é necessário corrigir os erros
                com a sua integração.
              </>
            }
          />
        </div>
      )}

      {webhook?.status === HistoryStatus.ARCHIVED && (
        <div className='mt-6'>
          <Alert
            type='warning'
            className='text-sm'
            message='Este evento foi arquivado, para reenviar, basta utilizar o botão de ação de retentativa.'
          />
        </div>
      )}

      {webhook?.status === HistoryStatus.FAILED && (
        <div className='mt-6'>
          <Alert
            type='error'
            className='text-sm'
            message={
              <>
                Ao enviar esse evento para sua URL, ela retornou o status HTTP{' '}
                <strong>{webhook?.lastSend?.returnStatus}</strong>. Por favor, corrija os erros para que na próxima
                tentativa o evento seja recebido com sucesso.
              </>
            }
          />
        </div>
      )}
      <div className='mt-6'>
        <div className='text-lg font-semibold'>Histórico de envios</div>
        <div>
          {historic.length === 0 && (
            <div className='text-primary-light w-full py-6 text-center text-sm'>Nenhum histórico encontrado.</div>
          )}

          {historic.length > 0 && (
            <div>
              <MyTable>
                <MyTableHeader index={indexes} />

                <MyTableBody>
                  {historic.map(item => (
                    <MyTableRow key={item._id}>
                      <MyTableCol screenSizes={{ xs: 12, sm: 12 }} mobileOrder={1}>
                        <span
                          className={`px-1.5 py-[3px] text-white ${HTTP_STATUS_STYLE[solveStatusColor(item.returnStatus)] || ''}`}
                        >
                          {item.returnStatus}
                        </span>
                      </MyTableCol>
                      <MyTableCol screenSizes={{ xs: 12, sm: 12 }} mobileOrder={2}>
                        <Truncate count={20} final={'...'}>
                          {item?.returnData || '-'}
                        </Truncate>
                      </MyTableCol>
                      <MyTableCol screenSizes={{ xs: 12, sm: 12 }} mobileOrder={3}>
                        {dateFormat(item.created_at, 'dd/MM/yyyy, HH:mm:ss')}
                      </MyTableCol>
                    </MyTableRow>
                  ))}
                </MyTableBody>
              </MyTable>
            </div>
          )}
        </div>
      </div>

      <div className='mt-6'>
        <div className='flex items-center'>
          <div className='mr-[5px] text-lg font-semibold'>Dados enviados</div>

          <Tooltip title={sent === sentData ? 'Copiado!' : 'Clique para copiar'}>
            <CopyOutlined className='cursor-pointer text-xl' onClick={e => handleSentDataCopy(e, sent)} />
          </Tooltip>
        </div>
        <div>
          <pre className='bg-grey-100 m-0 mt-2 w-full overflow-auto rounded-sm p-2 text-[11px]!'>{sent}</pre>
        </div>
      </div>
    </QuickViewDefault>
  );
};
